import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"

export interface SearchableSelectOption {
    label: string
    value: string
    icon?: React.ComponentType<{ className?: string, size?: number }>
}

interface SearchableSelectProps {
    value?: string | null
    onChange: (value: string) => void
    options: SearchableSelectOption[]
    placeholder?: string
    searchPlaceholder?: string
    emptyMessage?: string
    className?: string
    disabled?: boolean
    testId?: string
    side?: "top" | "bottom" | "left" | "right"
}

export interface SearchableSelectRef {
    open: () => void;
}

export const SearchableSelect = React.forwardRef<SearchableSelectRef, SearchableSelectProps>(({
    value,
    onChange,
    options,
    placeholder = "Select...",
    searchPlaceholder = "Search...",
    emptyMessage = "No option found.",
    className,
    disabled = false,
    testId,
    side,
}, ref) => {
    const [open, setOpen] = React.useState(false)
    const buttonRef = React.useRef<HTMLButtonElement>(null)

    React.useImperativeHandle(ref, () => ({
        open: () => {
            buttonRef.current?.focus()
            setOpen(true)
        }
    }));

    const selectedOption = options.find((option) => option.value === value)

    // Filter commands based on label to avoid "value" bleeding into search
    // Using custom filtering logic if needed, but default cmdk behavior is fuzzy search on text content (children) and value prop

    return (
        <Popover open={open} onOpenChange={setOpen} modal={true}>
            <PopoverTrigger asChild>
                <Button
                    ref={buttonRef}
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn("w-full justify-between font-normal px-3", !value && "text-muted-foreground", className)}
                    disabled={disabled}
                    data-testid={testId}
                >
                    {selectedOption ? (
                        <div className="flex items-center gap-2 truncate">
                            {selectedOption.icon && <selectedOption.icon size={14} className="shrink-0 text-yellow-500 fill-current" />}
                            <span className="truncate">{selectedOption.label}</span>
                        </div>
                    ) : (
                        placeholder
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[200px] p-0 z-[9999]" align="start" side={side} collisionPadding={16} style={{ width: 'var(--radix-popover-trigger-width)' }}>
                <Command>
                    <CommandInput placeholder={searchPlaceholder} />
                    <CommandList className="max-h-[220px] overflow-y-auto overscroll-contain touch-pan-y pointer-events-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent">
                        <CommandEmpty>{emptyMessage}</CommandEmpty>
                        <CommandGroup>
                            {options.map((option) => (
                                <CommandItem
                                    key={option.value}
                                    value={option.label}
                                    onSelect={() => {
                                        onChange(option.value)
                                        setOpen(false)
                                    }}
                                    className="cursor-pointer"
                                >
                                    <Check
                                        className={cn(
                                            "mr-2 h-4 w-4",
                                            value === option.value ? "opacity-100" : "opacity-0"
                                        )}
                                    />
                                    {option.icon && <option.icon size={14} className="mr-2 shrink-0 text-yellow-500 fill-current" />}
                                    {option.label}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
})
SearchableSelect.displayName = "SearchableSelect"
